import React, { useEffect, useState } from "react";
import { FaArrowLeft, FaPrint } from "react-icons/fa";
import FullPageSpinner from "../../components/FullPageSpinner";
import { sendRequest } from "../../utils/api";
import { useToast } from "../../context/ToastContext";

type ShiftType = "day" | "night" | "leave" | "";

type DailyShift = {
    day: ShiftType;
    branch: string;
};

type RosterEntry = {
    name: string;
    role: string;
    schedule: {
        monday: DailyShift;
        tuesday: DailyShift;
        wednesday: DailyShift;
        thursday: DailyShift;
        friday: DailyShift;
        saturday: DailyShift;
        sunday: DailyShift;
    };
};

type RosterProps = {
    data: RosterEntry[];
};

const Roster: React.FC<RosterProps> = ({ data }) => {
    const [selectedBranch, setSelectedBranch] = useState<string>("All");

    const getShiftColor = (shift: ShiftType) => {
        switch (shift) {
            case "day":
                return "bg-green-100 text-green-800";
            case "night":
                return "bg-blue-100 text-blue-800";
            case "leave":
                return "bg-red-100 text-red-700";
            default:
                return "bg-gray-100 text-gray-400";
        }
    };

    const terminalType = (terminal: string) => {
        if (terminal === "t1") return "Hotel";
        else if (terminal === "t2") return "Lounge";
    }

    // ✅ Extract branches dynamically
    const branches = Array.from(
        new Set(
            data.flatMap((staff) =>
                Object.values(staff.schedule)
                    .map((s) => s.branch)
                    .filter(Boolean)
            )
        )
    );

    // ✅ Filter by branch
    const filteredData =
        selectedBranch === "All"
            ? data
            : data.filter((staff) =>
                Object.values(staff.schedule).some(
                    (day) => day.branch === selectedBranch
                )
            );

    const printPage = () => window.print();

    return (
        <div className="p-4">
            {/* Header */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-3">
                <h1 className="text-3xl font-bold text-gray-800">Staff Roster</h1>

                <div className="flex items-center gap-3 print:hidden">
                    {/* <select
                        value={selectedBranch}
                        onChange={(e) => setSelectedBranch(e.target.value)}
                        className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
                    >
                        <option value="All">All Branches</option>
                        {branches.map((b) => (
                            <option key={b} value={b}>
                                {b}
                            </option>
                        ))}
                    </select> */}

                    <button
                        onClick={printPage}
                        className="flex items-center gap-2 bg-primary text-white px-4 py-2 rounded hover:bg-hoverButton transition"
                    >
                        {FaPrint({})}
                        Print
                    </button>
                </div>
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
                <table className="min-w-full border-collapse text-sm">
                    <thead>
                        <tr className="bg-primary text-white">
                            <th className="p-3 text-left">Name</th>
                            <th className="p-3 text-left">Role</th>
                            <th className="p-3 text-center">Mon</th>
                            <th className="p-3 text-center">Tue</th>
                            <th className="p-3 text-center">Wed</th>
                            <th className="p-3 text-center">Thu</th>
                            <th className="p-3 text-center">Fri</th>
                            <th className="p-3 text-center">Sat</th>
                            <th className="p-3 text-center">Sun</th>
                        </tr>
                    </thead>

                    <tbody>
                        {filteredData.length > 0 ? (
                            filteredData.map((staff, i) => (
                                <tr
                                    key={i}
                                    className={`border-b ${i % 2 === 0 ? "bg-gray-100" : "bg-white"
                                        }`}
                                >
                                    <td className="p-3 font-medium">{staff.name}</td>
                                    <td className="p-3 text-gray-700">{staff.role}</td>

                                    {Object.values(staff.schedule).map((shift, j) => (
                                        <td key={j} className="p-2 text-center">
                                            {!shift.day ? (
                                                <span className="text-gray-400 text-xs">—</span>
                                            ) : (
                                                <div
                                                    className={`inline-flex flex-col items-center px-2 py-1 rounded-full text-xs font-semibold ${getShiftColor(
                                                        shift.day
                                                    )}`}
                                                >
                                                    <span>{shift.day}</span>

                                                    {shift.branch && (
                                                        <span className="text-[10px] font-normal mt-0.5 opacity-80 leading-tight">
                                                            {terminalType(shift.branch)}
                                                        </span>
                                                    )}
                                                </div>

                                            )}
                                        </td>
                                    ))}
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan={9} className="text-center py-6 italic text-gray-500">
                                    No roster data
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};


export default function RosterPage() {
    const { showToast } = useToast();
    const [rosters, setRosters] = useState<RosterEntry[]>([]);

    useEffect(() => {
        const fetchRoster = async () => {
            try {
                const data = await sendRequest("GET", "/users/rosters");
                setRosters(data);
            } catch (err) {
                showToast("Failed to load roster. Try again.", { type: "error" });
            }
        };

        fetchRoster();
    }, []);

    return <Roster data={rosters} />;
}
